/**
 * New account's time zones (slice 8h, ADR 0042; Q87; its fix round): the list the login offers and the zone selected
 * at first.
 *
 * - The list is this browser's `Intl.supportedValuesOf('timeZone')` without the `Etc/*` zones (`Etc/GMT+5` is UTC−5:
 *   POSIX inverts the sign, which would mislead a player; `Etc/UTC` is `UTC`), with `UTC` once, first.
 * - The zone selected at first is the device's (`Intl.DateTimeFormat().resolvedOptions().timeZone`) only when the list
 *   has it; otherwise `UTC` (a zone such as `Etc/Unknown`, which some locked-down machines report, would only be
 *   refused by the server).
 * - Every `Intl` call is guarded: a browser without `Intl`, without the list (Safari before 15.4), or whose
 *   `DateTimeFormat` throws still gets the login, with `UTC` selected (alone when there is no list).
 *
 * The server checks the choice against its own `Intl` and keeps the name its list spells (`bad_time_zone` otherwise).
 */

/** The parts of `Intl` this reads (the tests pass a stand-in, or nothing). */
export type ZoneIntl = {
  readonly supportedValuesOf?: (key: 'timeZone') => readonly string[];
  readonly DateTimeFormat?: () => { resolvedOptions(): { readonly timeZone?: unknown } };
};

const deviceZone = (intl: ZoneIntl | undefined): string | null => {
  try {
    const named: unknown = intl?.DateTimeFormat?.().resolvedOptions().timeZone;
    return typeof named === 'string' && named !== '' ? named : null;
  } catch {
    return null;
  }
};

const listedZones = (intl: ZoneIntl | undefined): readonly string[] => {
  try {
    const list = intl?.supportedValuesOf?.('timeZone');
    return Array.isArray(list) ? list.filter((z): z is string => typeof z === 'string') : [];
  } catch {
    return [];
  }
};

/** The zones New account lists and the one selected at first (see the module's comment). */
export const timeZoneChoices = (intl: ZoneIntl | undefined): { readonly zones: readonly string[]; readonly device: string } => {
  const zones = ['UTC', ...listedZones(intl).filter((z) => z !== 'UTC' && !z.startsWith('Etc/'))];
  const device = deviceZone(intl);
  return { zones, device: device !== null && zones.includes(device) ? device : 'UTC' };
};
